import { styled, keyframes } from 'styled-components';
import React, { FC, PropsWithChildren, useEffect, useState } from 'react';
import { Styling } from '@/styling';
import { StandardCard } from '@/components/card/standard';

const Container = styled(StandardCard)`
  position: relative;
  border-radius: 4px;
  padding: 0;
  margin: 0;
  width: 100%;
  overflow: hidden;
  background: linear-gradient(
    10deg,
    #6acf47 25%,
    #268903 100%
  ); // TODO move to theme
`;

const InnerContainer = styled.div`
  position: relative;
  padding: ${Styling.spacing(3)};
  @media (max-width: ${Styling.breakpoint('sm')}px) {
    padding: ${Styling.spacing(2)};
  }
`;

const Overlay = styled.div`
  position: absolute;
  height: 100%;
  width: 100%;
  background: radial-gradient(
    circle,
    ${({ theme }) => theme.palette.primary.main} 0%,
    #fdb72a 100%
  ); // TODO move to theme
  opacity: 0;
  transition: opacity 200ms;

  &.visible {
    opacity: 1;
  }
`;

const CircleArea = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  overflow: hidden;
`;

const float = (height: string) => keyframes`
  from {
    transform: translate(-50%, calc(-50% - ${height}));
  }
  to {
    transform: translate(-50%, calc(-50% + ${height}));
  }
`;

type CircleProps = {
  size: number;
  x: number;
  y: number;
  moveHeight: number;
};

const Circle = styled.div<CircleProps>`
  position: absolute;
  width: ${({ size }) => size}px;
  height: ${({ size }) => size}px;
  top: ${({ y }) => y * 100}%;
  left: ${({ x }) => x * 100}%;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.palette.grayscale.white};
  opacity: 0.1;
  animation-name: ${({ moveHeight }) => float(`${moveHeight}%`)};
  animation-duration: ${({ moveHeight, size }) =>
    moveHeight / 10 + size / 100 + 2}s;
  animation-direction: alternate-reverse;
  animation-timing-function: ease-in-out;
  animation-iteration-count: infinite;
`;

export type AuthCardProps = PropsWithChildren<{
  className?: string;
  success?: boolean;
  circleCount?: number;
  circleMaxSize?: number;
  circleMinSize?: number;
}>;

export const AuthCard: FC<AuthCardProps> = ({
  className,
  success,
  circleCount = 5,
  circleMaxSize = 300,
  circleMinSize = 100,
  children,
}) => {
  const [containerElement, setContainerElement] =
    useState<HTMLDivElement | null>(null);
  const [circles, setCircles] = useState<CircleProps[]>([]);

  useEffect(() => {
    if (!containerElement) {
      return;
    }

    const arr: CircleProps[] = [];

    for (let i = 0; i < circleCount; i++) {
      arr.push({
        size: Math.random() * circleMaxSize + circleMinSize,
        x: Math.random(),
        y: Math.random(),
        moveHeight: 10 + Math.random() * 20,
      });
    }

    setCircles(arr);
  }, [containerElement]);

  return (
    <Container className={className} ref={(el) => setContainerElement(el)}>
      <Overlay className={success ? '' : 'visible'} />
      <CircleArea>
        {circles.map((props, i) => (
          <Circle key={i} {...props} />
        ))}
      </CircleArea>
      <InnerContainer>{children}</InnerContainer>
    </Container>
  );
};
